import Flickity from 'flickity';
import * as Sqrl from 'squirrelly';
import {formatMoney} from '@shopify/theme-currency';

import {register} from '../vendor/theme-scripts/theme-sections';
import throttle from '../util/throttle';
import debounce from '../util/debounce';
import slideUp from '../util/slide-up';
import slideDown from '../util/slide-down';
import FetchError from '../util/fetch-error';
import {Collapsible} from '../features/collapsible';
import QuantityCounter from '../features/quantity-handle';
import QuickViewPopup from '../features/quick-view-popup';
import {NotificationPopup} from '../features/notification-popup';
import {a11y} from '../vendor/theme-scripts/theme-a11y';

const settings = {
  cartDrawerEnabled: window.theme.settings.cartType === 'drawer',
  timers: {
    addProductTimeout: 1000,
    itemRemovalDelay: 500,
  },
  animations: {
    data: 'data-aos',
    method: 'fade-up',
  },
};

const selectors = {
  outerSection: '[data-section-id]',
  aos: '[data-aos]',
  additionalCheckoutButtons: '[data-additional-checkout-button]',
  apiContent: '[data-api-content]',
  apiLineItems: '[data-api-line-items]',
  apiUpsellItems: '[data-api-upsell-items]',
  buttonAddToCart: '[data-add-to-cart]',
  upsellButtonByHandle: '[data-handle]',
  cartCloseError: '[data-cart-error-close]',
  cartDrawer: '[data-cart-drawer]',
  cartDrawerTemplate: '[data-cart-drawer-template]',
  cartDrawerToggle: '[data-cart-drawer-toggle]',
  cartDrawerBody: '[data-cart-drawer-body]',
  cartErrors: '[data-cart-errors]',
  cartForm: '[data-cart-form]',
  cartTermsCheckbox: '[data-cart-acceptance-checkbox]',
  cartCheckoutButtonWrapper: '[data-cart-checkout-buttons]',
  cartCheckoutButton: '[data-cart-checkout-button]',
  cartTotalDiscountsTemplate: '[data-cart-total-discount]',
  cartItemRemove: '[data-item-remove]',
  cartItemQty: '[data-quantity-field]',
  cartItemsQty: '[data-cart-items-qty]',
  cartTotal: '[data-cart-total]',
  cartMessage: '[data-cart-message]',
  cartMessageDefault: '[data-message-default]',
  cartPage: '[data-cart-page]',
  cartProgress: '[data-cart-message-progress]',
  cartOriginalTotal: '[data-cart-original-total]',
  cartOriginalTotalPrice: '[data-cart-original-total-price]',
  cartDiscountsHolder: '[data-cart-discounts-holder]',
  emptyMessage: '[data-empty-message]',
  buttonHolder: '[data-foot-holder]',
  item: '[data-cart-item]',
  itemsHolder: '[data-items-holder]',
  itemsWrapper: '[data-items-wrapper]',
  formCloseError: '[data-close-error]',
  formErrorsContainer: '[data-cart-errors-container]',
  upsellHolder: '[data-upsell-holder]',
  errorMessage: '[data-error-message]',
  termsErrorMessage: '[data-terms-error-message]',
  pairProductsHolder: '[data-pair-products-holder]',
  pairProducts: '[data-pair-products]',
  productIDAttribute: 'data-product-id',
  leftToSpend: '[data-left-to-spend]',
  quickBuyForm: '[data-quickbuy-form]',
  productMediaContainer: '[data-product-media-container]',
  formWrapper: '[data-form-wrapper]',
  productForm: '[data-product-form]',
  popupQuickView: '.popup-quick-view',
  popupClose: '[data-popup-close]',
  error: '[data-error]',
  quickViewOnboarding: '[data-quick-view-onboarding]',
  flickityEnabled: '.flickity-enabled',
};

const classes = {
  hidden: 'hidden',
  added: 'is-added',
  isHidden: 'is-hidden',
  cartDrawerOpen: 'js-drawer-open-cart',
  open: 'is-open',
  visible: 'is-visible',
  loading: 'is-loading',
  disabled: 'is-disabled',
  success: 'is-success',
  error: 'has-error',
  cartItems: 'cart-toggle-has-items',
  variantSoldOut: 'variant--soldout',
  removed: 'is-removed',
  aosAnimate: 'aos-animate',
  updated: 'is-updated',
  noOutline: 'no-outline',
  productGridImageError: 'product-grid-item__image--error',
};

const attributes = {
  shippingMessageLimit: 'data-limit',
  cartMessageValue: 'data-cart-message',
  cartTotalPrice: 'data-cart-total-price',
  ariaExpanded: 'aria-expanded',
  disabled: 'disabled',
  value: 'value',
  dataId: 'data-id',
  focusElement: 'data-focus-element',
  upsellButton: 'data-upsell-btn',
  notificationPopup: 'data-notification-popup',
  sectionId: 'data-section-id',
};

let sections = {};

class CartDrawer {
  constructor() {
    if (window.location.pathname === '/password') {
      return;
    }

    this.init();
  }

  init() {
    // DOM Elements
    this.cartToggleButtons = document.querySelectorAll(selectors.cartDrawerToggle);
    this.cartPage = document.querySelector(selectors.cartPage);
    this.cartDrawer = document.querySelector(selectors.cartDrawer);
    this.cart = this.cartDrawer || this.cartPage;

    this.assignArguments();

    this.flktyUpsell = null;
    this.form = null;
    this.collapsible = null;
    this.a11y = a11y;

    this.build = this.build.bind(this);

    // AJAX request
    this.addToCart = this.addToCart.bind(this);
    this.updateCart = this.updateCart.bind(this);

    // Cart events
    this.openCartDrawer = this.openCartDrawer.bind(this);
    this.closeCartDrawer = this.closeCartDrawer.bind(this);
    this.toggleCartDrawer = this.toggleCartDrawer.bind(this);
    this.formSubmitHandler = throttle(this.formSubmitHandler.bind(this), 50);
    this.closeCartError = debounce(() => slideUp(this.cartErrorHolder, 400), 250);
    this.cartDrawerCloseEvent = null;

    // Checking
    this.hasItemsInCart = this.hasItemsInCart.bind(this);
    this.isCartPage = Boolean(this.cartDrawer === null);
    this.showAnimations = Boolean(document.body.dataset.animations === 'true');

    // Set classes
    this.toggleClassesOnContainers = this.toggleClassesOnContainers.bind(this);

    // Flags
    this.totalItems = 0;
    this.isCartDrawerOpen = false;
    this.isCartDrawerLoaded = false;
    this.cartDiscounts = 0;
    this.cartDrawerEnabled = settings.cartDrawerEnabled;
    this.cartLimitErrorIsHidden = true;
    this.cartAnimationTimer = 0;

    // Cart Events
    this.cartEvents();
    this.cartAddEvent();
    this.cartDrawerToggleEvents();

    // Init quantity for fields
    this.initQuantity();

    // Init collapsible function for the cart accordions
    if (this.buttonHolder) {
      this.collapsible = new Collapsible(this.buttonHolder);
    }

    if (this.isCartPage) {
      this.renderPairProducts();
    }

    document.addEventListener('theme:popup:open', this.closeCartDrawer);
  }

  /**
   * Assign cart constructor arguments on page load or after cart drawer is loaded
   *
   * @return  {Void}
   */
  assignArguments() {
    this.cartDrawerBody = document.querySelector(selectors.cartDrawerBody);
    this.emptyMessage = document.querySelector(selectors.emptyMessage);
    this.buttonHolder = document.querySelector(selectors.buttonHolder);
    this.itemsHolder = document.querySelector(selectors.itemsHolder);
    this.cartItemsQty = document.querySelector(selectors.cartItemsQty);
    this.itemsWrapper = document.querySelector(selectors.itemsWrapper);
    this.items = document.querySelectorAll(selectors.item);
    this.cartTotal = document.querySelector(selectors.cartTotal);
    this.cartMessage = document.querySelectorAll(selectors.cartMessage);
    this.cartOriginalTotal = document.querySelector(selectors.cartOriginalTotal);
    this.cartOriginalTotalPrice = document.querySelector(selectors.cartOriginalTotalPrice);
    this.cartTotalDiscountTemplate = document.querySelector(selectors.cartTotalDiscountsTemplate).innerHTML;
    this.cartErrorHolder = document.querySelector(selectors.cartErrors);
    this.cartCloseErrorMessage = document.querySelector(selectors.cartCloseError);
    this.pairProductsHolder = document.querySelector(selectors.pairProductsHolder);
    this.pairProducts = document.querySelector(selectors.pairProducts);
    this.upsellHolders = document.querySelectorAll(selectors.upsellHolder);
    this.cartTermsCheckbox = document.querySelector(selectors.cartTermsCheckbox);
    this.cartCheckoutButtonWrapper = document.querySelector(selectors.cartCheckoutButtonWrapper);
    this.cartCheckoutButton = document.querySelector(selectors.cartCheckoutButton);
    this.cartForm = document.querySelector(selectors.cartForm);
    this.cartItemCount = 0;
    this.button = null;

    if (this.cartMessage.length > 0) {
      this.cartFreeLimitShipping = Number(this.cartMessage[0].getAttribute(attributes.shippingMessageLimit)) * 100 * window.Shopify.currency.rate;
      this.subtotal = Number(this.cartMessage[0].getAttribute(attributes.cartTotalPrice));
    }

    this.updateProgress();
  }

  /**
   * Init quantity field functionality
   *
   * @return  {Void}
   */

  initQuantity() {
    this.items = document.querySelectorAll(selectors.item);

    if (this.items.length) {
      this.items.forEach((item) => {
        const quantity = new QuantityCounter(item, true);

        quantity.init();
        this.customEventsHandle(item);
      });
    }
  }

  /**
   * Custom event who change the cart
   *
   * @return  {Void}
   */

  customEventsHandle(holder) {
    holder.addEventListener(
      'theme:cart:update',
      debounce((event) => {
        this.updateCart(
          {
            id: event.detail.id,
            quantity: event.detail.quantity,
          },
          holder,
          event.detail.valueIsEmpty
        );
      }, 250)
    );
  }

  /**
   * Cart events
   *
   * @return  {Void}
   */

  cartEvents() {
    const cartItemRemove = document.querySelectorAll(selectors.cartItemRemove);

    if (cartItemRemove.length) {
      this.totalItems = cartItemRemove.length;
      cartItemRemove.forEach((item) => {
        item.addEventListener('click', (event) => {
          event.preventDefault();
          const cartItem = item.closest(selectors.item);

          this.updateCart(
            {
              id: item.getAttribute(attributes.dataId),
              quantity: 0,
            },
            cartItem
          );
        });
      });
    }

    if (this.cartCloseErrorMessage) {
      this.cartCloseErrorMessage.removeEventListener('click', this.closeCartError);
      this.cartCloseErrorMessage.addEventListener('click', this.closeCartError);
    }

    if (this.cartTermsCheckbox) {
      this.cartTermsCheckbox.removeEventListener('change', this.formSubmitHandler);
      this.cartCheckoutButtonWrapper.removeEventListener('click', this.formSubmitHandler);
      this.cartForm.removeEventListener('submit', this.formSubmitHandler);

      this.cartTermsCheckbox.addEventListener('change', this.formSubmitHandler);
      this.cartCheckoutButtonWrapper.addEventListener('click', this.formSubmitHandler);
      this.cartForm.addEventListener('submit', this.formSubmitHandler);
    }
  }

  /**
   * Cart event add product to cart
   *
   * @return  {Void}
   */

  cartAddEvent() {
    document.addEventListener('click', (event) => {
      const clickedElement = event.target;
      const isButtonATC = clickedElement?.matches(selectors.buttonAddToCart);
      const getButtonATC = clickedElement?.closest(selectors.buttonAddToCart);

      if (isButtonATC || getButtonATC) {
        event.preventDefault();

        this.button = isButtonATC ? clickedElement : getButtonATC;
        this.form = clickedElement.closest('form');
        this.formWrapper = this.button.closest(selectors.formWrapper);
        const isVariantSoldOut = this.formWrapper?.classList.contains(classes.variantSoldOut);
        const isButtonDisabled = this.button.hasAttribute(attributes.disabled);
        const isQuickViewOnboarding = this.button.closest(selectors.quickViewOnboarding);
        const hasProductId = this.button.hasAttribute(selectors.productIDAttribute);
        const hasNotificationPopup = this.button.hasAttribute(attributes.notificationPopup);
        const hasFileInput = this.form?.querySelector('[type="file"]');
        let formData = '';

        if (isButtonDisabled || hasFileInput || isQuickViewOnboarding) {
          return;
        }

        // Notification popup
        if (isVariantSoldOut && hasNotificationPopup) {
          new NotificationPopup(this.button);
          return;
        }

        // Form Data
        if (hasProductId) {
          formData = `id=${Number(this.button.getAttribute(selectors.productIDAttribute))}`;
        } else {
          formData = new FormData(this.form);
          formData = new URLSearchParams(formData).toString();
        }

        this.addToCart(formData);

        // Hook for cart/add.js event
        document.dispatchEvent(
          new CustomEvent('theme:cart:add', {
            bubbles: true,
            detail: {
              selector: clickedElement,
            },
          })
        );
      }
    });
  }

  /**
   * Get response from the cart
   *
   * @return  {Void}
   */

  getCart() {
    // Render cart drawer if it exists but it's not loaded yet
    if (this.cartDrawer && !this.isCartDrawerLoaded) {
      const alwaysOpen = false;
      this.renderCartDrawer(alwaysOpen);
    }

    fetch(theme.routes.root + 'cart.js')
      .then(this.handleErrors)
      .then((response) => response.json())
      .then((response) => {
        this.cartItemCount = response.item_count;
        this.newTotalItems = response.items.length;

        this.buildTotalPrice(response);

        if (this.cartMessage.length > 0) {
          this.subtotal = response.total_price;
          this.updateProgress();
        }

        return fetch(theme.routes.root + 'cart?section_id=api-cart-items');
      })
      .then((response) => response.text())
      .then((response) => {
        const element = document.createElement('div');
        element.innerHTML = response;

        this.cartToggleButtons.forEach((button) => {
          button.classList.remove(classes.cartItems);

          if (this.newTotalItems > 0) {
            button.classList.add(classes.cartItems);
          }
        });

        const cleanResponse = element.querySelector(selectors.apiContent);
        this.build(cleanResponse);

        this.updateItemsQuantity(this.cartItemCount);
      })
      .catch((error) => console.log(error));
  }

  /**
   * Add item(s) to the cart and show the added item(s)
   *
   * @param   {String}  data
   * @param   {DOM Element}  button
   *
   * @return  {Void}
   */

  addToCart(data) {
    if (this.cartDrawerEnabled && this.button) {
      this.button.classList.add(classes.loading);
      this.button.setAttribute(attributes.disabled, true);
    }

    fetch(theme.routes.root + 'cart/add.js', {
      method: 'POST',
      headers: {
        'X-Requested-With': 'XMLHttpRequest',
        'Content-Type': 'application/x-www-form-urlencoded',
      },
      body: data,
    })
      .then((response) => response.json())
      .then((response) => {
        this.button.disabled = true;
        this.addLoadingClass();

        if (response.status) {
          this.addToCartError(response);

          this.removeLoadingClass();

          return;
        }

        if (this.cartDrawerEnabled) {
          this.getCart();
        } else {
          window.location = theme.routes.cart_url;
        }
      })
      .catch((error) => console.log(error));
  }

  /**
   * Update cart
   *
   * @param   {Object}  updateData
   *
   * @return  {Void}
   */

  updateCart(updateData = {}, holder = null, valueIsEmpty = false) {
    let newCount = null;
    let oldCount = null;
    let newItem = null;
    let quantity = updateData.quantity;

    this.addLoadingClass();

    // Handle item removal
    if (quantity == 0 && holder) {
      holder.classList.add(classes.removed);
      setTimeout(() => {
        holder.remove();
      }, settings.timers.itemRemovalDelay);
    }

    this.items.forEach((item) => {
      item.classList.add(classes.disabled);
      item.querySelector('input').blur();
      item.querySelectorAll('input, button').forEach((el) => {
        el.setAttribute('disabled', true);
      });
    });

    fetch(theme.routes.root + 'cart.js')
      .then(this.handleErrors)
      .then((response) => response.json())
      .then((response) => {
        const matchKeys = (item) => item.key === updateData.id;
        const index = response.items.findIndex(matchKeys);
        oldCount = response.item_count;
        newItem = response.items[index].title;

        const data = {
          line: `${index + 1}`,
          quantity: quantity,
        };

        return fetch(theme.routes.root + 'cart/change.js', {
          method: 'post',
          headers: {'Content-Type': 'application/json'},
          body: JSON.stringify(data),
        });
      })
      .then(this.handleErrors)
      .then((response) => response.json())
      .then((response) => {
        newCount = response.item_count;
        this.cartItemCount = newCount;

        if (valueIsEmpty) {
          quantity = 1;
        }

        if (quantity !== 0) {
          this.cartLimitErrorIsHidden = newCount !== oldCount;

          this.toggleLimitError(newItem);
        }

        // Change the cart total, total items quantity and hide message if missing discounts and the changed product is not deleted
        this.buildTotalPrice(response);
        this.cartDiscounts = response.total_discount;
        this.updateItemsQuantity(this.cartItemCount);

        // Build cart again if the quantity of the changed product is 0 or cart discounts are changed
        if (this.cartMessage.length > 0) {
          this.subtotal = response.total_price;
          this.updateProgress();
        }

        this.getCart();
      })
      .catch((error) => console.log(error));
  }

  /**
   * Show/hide limit error
   *
   * @param   {String}  itemTitle
   *
   * @return  {Void}
   */

  toggleLimitError(itemTitle) {
    this.cartErrorHolder.querySelector(selectors.errorMessage).innerText = itemTitle;

    if (this.cartLimitErrorIsHidden) {
      slideUp(this.cartErrorHolder, 400);
    } else {
      slideDown(this.cartErrorHolder, 400);
    }
  }

  /**
   * Handle errors
   *
   * @param   {Object}  response
   *
   * @return  {Object}
   */

  handleErrors(response) {
    if (!response.ok) {
      return response.json().then(function (json) {
        const e = new FetchError({
          status: response.statusText,
          headers: response.headers,
          json: json,
        });
        throw e;
      });
    }
    return response;
  }

  /**
   * Add to cart error handle
   *
   * @param   {Object}  data
   * @param   {DOM Element/Null} button
   *
   * @return  {Void}
   */

  addToCartError(data) {
    const buttonQuickBuyForm = this.button.closest(selectors.quickBuyForm);
    const buttonUpsellHolder = this.button.closest(selectors.upsellHolder);
    const isFocusEnabled = !document.body.classList.contains(classes.noOutline);
    // holder: Product form containers or Upsell products in Cart form
    let holder = this.button.closest(selectors.productForm) ? this.button.closest(selectors.productForm) : this.button.closest(selectors.upsellHolder);
    let errorContainer = holder.querySelector(selectors.formErrorsContainer);

    // Upsell products in Cart form
    if (buttonUpsellHolder) {
      errorContainer = buttonUpsellHolder.querySelector(selectors.formErrorsContainer);
    }

    if (this.cartDrawerEnabled && this.button && this.button.closest(selectors.cartDrawer) !== null && !this.button.closest(selectors.cartDrawer)) {
      this.closeCartDrawer();
    }

    this.button.classList.remove(classes.loading);
    this.button.removeAttribute(attributes.disabled);

    // Error message content
    const closeErrorButton = buttonQuickBuyForm
      ? ''
      : `
      <button type="button" class="errors__button-close" data-close-error>
        ${theme.icons.close}
      </button>
    `;

    errorContainer.innerHTML = `
      <div class="errors" data-error>
        ${data.message}: ${data.description}
        ${closeErrorButton}
      </div>
    `;

    // Quick buy in PGI errors
    if (buttonQuickBuyForm) {
      const productMediaContainer = errorContainer.closest(selectors.productMediaContainer);
      productMediaContainer.classList.add(classes.productGridImageError);

      errorContainer.querySelector(selectors.error).addEventListener('animationend', () => {
        productMediaContainer.classList.remove(classes.productGridImageError);
        errorContainer.innerHTML = '';

        if (!isFocusEnabled) {
          document.activeElement.blur();
        }
      });
    } else {
      // PDP form, Quick view popup forms and Upsell sliders errors
      errorContainer.classList.add(classes.visible);
      errorContainer.addEventListener('transitionend', () => {
        this.resizeSliders(errorContainer);
      });

      this.handleCloseErrorMessages(errorContainer);
    }
  }

  /**
   * Handle close buttons in error messages containers
   *
   * @param   {Object}  The error container that holds the close button
   * @return  {Void}
   */
  handleCloseErrorMessages(container) {
    const formErrorClose = container.querySelector(selectors.formCloseError);

    formErrorClose.addEventListener('click', (event) => {
      const clickedElement = event.target;
      const isFormCloseError = clickedElement.matches(selectors.formCloseError) || clickedElement.closest(selectors.formCloseError);

      if (!isFormCloseError) return;

      event.preventDefault();
      container.classList.remove(classes.visible);
      container.querySelector(selectors.error).addEventListener('transitionend', () => {
        container.innerHTML = '';
        this.resizeSliders(clickedElement);
      });
    });
  }

  /**
   * Resize sliders height
   *
   * @param   {Object}  Element within the slider container that would be resized
   * @return  {Void}
   */
  resizeSliders(element) {
    const slider = element.closest(selectors.flickityEnabled);

    if (!slider) return;

    const flkty = Flickity.data(slider);
    requestAnimationFrame(() => flkty.resize());
  }

  /**
   * Render cart and define all elements after cart drawer is open for a first time
   *
   * @return  {Void}
   */
  renderCartDrawer(alwaysOpen = true) {
    const cartDrawerTemplate = document.querySelector(selectors.cartDrawerTemplate);

    if (!cartDrawerTemplate) {
      return;
    }

    // Append cart items HTML to the cart drawer container
    this.cartDrawer.innerHTML = cartDrawerTemplate.innerHTML;
    this.assignArguments();

    // Bind cart quantity events
    this.initQuantity();

    // Bind cart events
    this.cartEvents();

    // Init collapsible function for the cart drawer accordions
    if (this.buttonHolder) {
      this.collapsible = new Collapsible(this.buttonHolder);
    }

    // Bind cart drawer close button event
    this.cartDrawerToggle = this.cartDrawer.querySelector(selectors.cartDrawerToggle);
    this.cartDrawerToggle.addEventListener('click', this.cartDrawerToggleClickEvent);

    this.isCartDrawerLoaded = true;

    this.renderPairProducts();

    // Hook for cart drawer loaded event
    document.dispatchEvent(new CustomEvent('theme:cart:loaded', {bubbles: true}));

    // Open cart drawer after cart items and events are loaded
    if (alwaysOpen) {
      this.openCartDrawer();
    }
  }

  /**
   * Open cart dropdown and add class on body
   *
   * @return  {Void}
   */

  openCartDrawer() {
    if (this.isCartDrawerOpen) {
      return;
    }

    if (!this.isCartDrawerLoaded) {
      this.renderCartDrawer();
      return;
    }

    // Hook for cart drawer open event
    document.dispatchEvent(new CustomEvent('theme:cart:open', {bubbles: true}));
    document.dispatchEvent(new CustomEvent('theme:scroll:lock', {bubbles: true, detail: this.cartDrawer}));
    document.dispatchEvent(new CustomEvent('theme:scroll:lock', {bubbles: true, detail: this.cartDrawerBody}));

    document.body.classList.add(classes.cartDrawerOpen);
    this.cartDrawer.classList.add(classes.open);

    // Cart elements opening animation
    this.cartDrawer.querySelectorAll(selectors.aos).forEach((item) => {
      item.classList.add(classes.aosAnimate);
    });

    this.cartToggleButtons.forEach((button) => {
      button.setAttribute(attributes.ariaExpanded, true);
    });

    this.a11y.trapFocus({
      container: this.cartDrawer,
    });

    // Observe Additional Checkout Buttons
    this.observeAdditionalCheckoutButtons();
    this.isCartDrawerOpen = true;
  }

  /**
   * Close cart dropdown and remove class on body
   *
   * @return  {Void}
   */

  closeCartDrawer() {
    if (!this.isCartDrawerOpen) {
      return;
    }

    // Hook for cart drawer close event
    document.dispatchEvent(new CustomEvent('theme:cart:close', {bubbles: true}));

    // Cart elements closing animation

    if (this.cartAnimationTimer) {
      clearTimeout(this.cartAnimationTimer);
    }

    this.cartAnimationTimer = setTimeout(() => {
      this.cartDrawer.querySelectorAll(selectors.aos).forEach((item) => {
        item.classList.remove(classes.aosAnimate);
      });
    }, 300);

    slideUp(this.cartErrorHolder, 400);

    this.a11y.removeTrapFocus();

    this.cartToggleButtons.forEach((button) => {
      button.setAttribute(attributes.ariaExpanded, false);
    });

    document.body.classList.remove(classes.cartDrawerOpen);
    this.cartDrawer.classList.remove(classes.open);
    this.itemsHolder.classList.remove(classes.updated);

    // Fixes header background update on cart-drawer close
    const isFocusEnabled = !document.body.classList.contains(classes.noOutline);
    if (!isFocusEnabled) {
      requestAnimationFrame(() => {
        document.activeElement.blur();
      });
    }

    // Enable page scroll right after the closing animation ends
    const timeout = 400;
    document.dispatchEvent(new CustomEvent('theme:scroll:unlock', {bubbles: true, detail: timeout}));

    this.isCartDrawerOpen = false;
  }

  /**
   * Toggle cart dropdown
   *
   * @return  {Void}
   */

  toggleCartDrawer() {
    if (this.isCartDrawerOpen) {
      this.closeCartDrawer();
    } else {
      this.openCartDrawer();
    }
  }

  /**
   * Cart drawer toggle events
   *
   * @return  {Void}
   */

  cartDrawerToggleEvents() {
    if (!this.cartDrawer) {
      return;
    }

    // Close cart drawer on ESC key pressed
    this.cartDrawer.addEventListener('keyup', (event) => {
      if (event.code === theme.keyboardKeys.ESCAPE) {
        this.closeCartDrawer();
      }
    });

    // Define cart drawer toggle button click event
    this.cartDrawerToggleClickEvent = (event) => {
      event.preventDefault();
      const button = event.target;

      if (button.getAttribute(attributes.ariaExpanded) === 'false') {
        this.a11y.state.trigger = button;
      }

      this.toggleCartDrawer();
    };

    // Define cart drawer close event
    this.cartDrawerCloseEvent = (event) => {
      const isCartDrawerToggle = event.target.matches(selectors.cartDrawerToggle);
      const isCartDrawerChild = document.querySelector(selectors.cartDrawer).contains(event.target);
      const isPopupQuickView = event.target.closest(selectors.popupQuickView);

      if (!isCartDrawerToggle && !isCartDrawerChild && !isPopupQuickView) {
        this.closeCartDrawer();
      }
    };

    // Bind cart drawer toggle buttons click event
    this.cartToggleButtons.forEach((button) => {
      button.addEventListener('click', this.cartDrawerToggleClickEvent);
    });

    // Close drawers on click outside
    //   Replaced 'click' with 'mousedown' as a quick and simple fix to the dragging issue on the upsell slider
    //   which was causing the cart-drawer to close when we start dragging the slider and finish our drag outside the cart-drawer
    //   which was triggering the 'click' event
    document.addEventListener('mousedown', this.cartDrawerCloseEvent);
  }

  /**
   * Toggle classes on different containers and messages
   *
   * @return  {Void}
   */

  toggleClassesOnContainers() {
    const that = this;

    this.emptyMessage.classList.toggle(classes.hidden, that.hasItemsInCart());
    this.buttonHolder.classList.toggle(classes.hidden, !that.hasItemsInCart());
    this.itemsHolder.classList.toggle(classes.hidden, !that.hasItemsInCart());
    this.cartItemsQty.classList.toggle(classes.hidden, !that.hasItemsInCart());
  }

  /**
   * Build cart depends on results
   *
   * @param   {Object}  data
   *
   * @return  {Void}
   */

  build(data) {
    const cartItemsData = data.querySelector(selectors.apiLineItems);
    const upsellItemsData = data.querySelector(selectors.apiUpsellItems);
    const cartEmptyData = Boolean(cartItemsData === null && upsellItemsData === null);

    if (this.totalItems !== this.newTotalItems) {
      this.totalItems = this.newTotalItems;

      this.toggleClassesOnContainers();
    }

    // Add class "is-updated" line items holder to reduce cart items animation delay via CSS variables
    if (this.isCartDrawerOpen) {
      this.itemsHolder.classList.add(classes.updated);
    }

    if (cartEmptyData) {
      this.itemsHolder.innerHTML = data;

      if (this.pairProductsHolder) {
        this.pairProductsHolder.innerHTML = '';
      }
    } else {
      this.itemsHolder.innerHTML = cartItemsData.innerHTML;

      if (this.pairProductsHolder) {
        this.pairProductsHolder.innerHTML = upsellItemsData.innerHTML;
      }

      this.renderPairProducts();
    }

    this.cartEvents();
    this.initQuantity();
    this.resetButtonClasses();
    this.removeLoadingClass();

    document.dispatchEvent(new CustomEvent('theme:cart:added', {bubbles: true}));

    if (this.cartDrawer) {
      this.openCartDrawer();
    }
  }

  /**
   * Check for items in the cart
   *
   * @return  {Void}
   */

  hasItemsInCart() {
    return this.totalItems > 0;
  }

  /**
   * Build total cart total price
   *
   * @param   {Object}  data
   *
   * @return  {Void}
   */

  buildTotalPrice(data) {
    const cartDiscountsHolder = document.querySelector(selectors.cartDiscountsHolder);

    if (data.original_total_price > data.total_price && data.cart_level_discount_applications.length > 0) {
      this.cartOriginalTotal.classList.remove(classes.hidden);
      if (data.original_total_price === 0) {
        this.cartOriginalTotalPrice.innerHTML = window.theme.strings.free;
      } else {
        this.cartOriginalTotalPrice.innerHTML = formatMoney(data.original_total_price, theme.moneyWithCurrencyFormat);
      }
    } else {
      this.cartOriginalTotal.classList.add(classes.hidden);
    }

    this.cartTotal.innerHTML = data.total_price === 0 ? window.theme.strings.free : formatMoney(data.total_price, theme.moneyWithCurrencyFormat);

    if (data.cart_level_discount_applications.length > 0) {
      const discountsMarkup = this.buildCartTotalDiscounts(data.cart_level_discount_applications);

      cartDiscountsHolder.classList.remove(classes.hidden);
      cartDiscountsHolder.innerHTML = discountsMarkup;
    } else {
      cartDiscountsHolder.classList.add(classes.hidden);
    }
  }

  /**
   * Build cart total discounts
   *
   * @param   {Array}  discounts
   *
   * @return  {String}
   */

  buildCartTotalDiscounts(discounts) {
    let discountMarkup = '';

    discounts.forEach((discount) => {
      discountMarkup += Sqrl.render(this.cartTotalDiscountTemplate, {
        discountTitle: discount.title,
        discountTotalAllocatedAmount: formatMoney(discount.total_allocated_amount, theme.moneyFormat),
      });
    });

    return discountMarkup;
  }

  /**
   * Show/hide free shipping message
   *
   * @param   {Number}  total
   *
   * @return  {Void}
   */

  freeShippingMessageHandle(total) {
    if (this.cartMessage.length > 0) {
      document.querySelectorAll(selectors.cartMessage).forEach((message) => {
        const hasFreeShipping = message.hasAttribute(attributes.cartMessageValue) && message.getAttribute(attributes.cartMessageValue) === 'true' && total !== 0;
        const cartMessageDefault = message.querySelector(selectors.cartMessageDefault);

        message.classList.toggle(classes.success, total >= this.cartFreeLimitShipping && hasFreeShipping);
        message.classList.toggle(classes.isHidden, total === 0);
        cartMessageDefault.classList.toggle(classes.isHidden, total >= this.cartFreeLimitShipping);
      });
    }
  }

  /**
   * Update progress when update cart
   *
   * @return  {Void}
   */

  updateProgress() {
    const newPercentValue = (this.subtotal / this.cartFreeLimitShipping) * 100;
    const leftToSpend = theme.settings.currency_code_enable
      ? formatMoney(this.cartFreeLimitShipping - this.subtotal, theme.moneyWithCurrencyFormat)
      : formatMoney(this.cartFreeLimitShipping - this.subtotal, theme.moneyFormat);

    if (this.cartMessage.length > 0) {
      document.querySelectorAll(selectors.cartMessage).forEach((message) => {
        const cartMessageProgressItems = message.querySelectorAll(selectors.cartProgress);
        const leftToSpendMessage = message.querySelector(selectors.leftToSpend);

        if (leftToSpendMessage) {
          leftToSpendMessage.innerHTML = leftToSpend.replace('.00', '').replace(',00', '');
        }

        if (cartMessageProgressItems.length) {
          cartMessageProgressItems.forEach((cartMessageProgress, index) => {
            cartMessageProgress.classList.toggle(classes.isHidden, this.subtotal / this.cartFreeLimitShipping >= 1);
            cartMessageProgress.style.setProperty('--progress-width', `${newPercentValue}%`);
            if (index === 0) {
              cartMessageProgress.setAttribute(attributes.value, newPercentValue);
            }
          });
        }

        this.freeShippingMessageHandle(this.subtotal);
      });
    }
  }

  /**
   * Render Upsell Products
   */
  renderPairProducts() {
    this.flktyUpsell = null;
    this.pairProductsHolder = document.querySelector(selectors.pairProductsHolder);
    this.pairProducts = document.querySelector(selectors.pairProducts);
    this.upsellHolders = document.querySelectorAll(selectors.upsellHolder);

    if (this.pairProductsHolder === null || this.pairProductsHolder === undefined) {
      return;
    }

    // Upsell slider
    const that = this;
    if (this.upsellHolders.length > 1) {
      this.flktyUpsell = new Flickity(this.pairProducts, {
        wrapAround: true,
        pageDots: true,
        adaptiveHeight: true,
        prevNextButtons: false,
        on: {
          ready: function () {
            new QuickViewPopup(that.cart);
            this.reloadCells();
            this.resize();
          },
        },
      });

      return;
    }

    // Single upsell item
    new QuickViewPopup(this.cart);
  }

  updateItemsQuantity(itemsQty) {
    let oneItemText = theme.strings.cart_items_one;
    let manyItemsText = theme.strings.cart_items_many;
    oneItemText = oneItemText.split('}}')[1];
    manyItemsText = manyItemsText.split('}}')[1];

    if (this.cartItemsQty) {
      this.cartItemsQty.textContent = itemsQty === 1 ? `${itemsQty} ${oneItemText}` : `${itemsQty} ${manyItemsText}`;
    }
  }

  observeAdditionalCheckoutButtons() {
    // identify an element to observe
    const additionalCheckoutButtons = this.cart.querySelector(selectors.additionalCheckoutButtons);
    if (additionalCheckoutButtons) {
      // create a new instance of `MutationObserver` named `observer`,
      // passing it a callback function
      const observer = new MutationObserver(() => {
        this.a11y.removeTrapFocus();
        this.a11y.trapFocus({
          container: this.cart,
        });
        observer.disconnect();
      });

      // call `observe()` on that MutationObserver instance,
      // passing it the element to observe, and the options object
      observer.observe(additionalCheckoutButtons, {subtree: true, childList: true});
    }
  }

  formSubmitHandler() {
    const termsAccepted = document.querySelector(selectors.cartTermsCheckbox).checked;
    const termsError = document.querySelector(selectors.termsErrorMessage);

    // Disable form submit if terms and conditions are not accepted
    if (!termsAccepted) {
      if (document.querySelector(selectors.termsErrorMessage).length > 0) {
        return;
      }

      termsError.innerText = theme.strings.cart_acceptance_error;
      this.cartCheckoutButton.setAttribute(attributes.disabled, true);
      slideDown(termsError, 400);
    } else {
      slideUp(termsError, 400);
      this.cartCheckoutButton.removeAttribute(attributes.disabled);
    }
  }

  resetButtonClasses() {
    const buttons = document.querySelectorAll(selectors.buttonAddToCart);
    if (buttons) {
      buttons.forEach((button) => {
        if (button.classList.contains(classes.loading)) {
          button.classList.remove(classes.loading);
          button.classList.add(classes.success);

          setTimeout(() => {
            button.removeAttribute(attributes.disabled);
            button.classList.remove(classes.success);
          }, settings.timers.addProductTimeout);
        }
      });
    }
  }

  addLoadingClass() {
    if (this.cartDrawer) {
      this.cartDrawer.classList.add(classes.loading);
    } else if (this.itemsWrapper) {
      this.itemsWrapper.classList.add(classes.loading);
    }
  }

  removeLoadingClass() {
    if (this.cartDrawer) {
      this.cartDrawer.classList.remove(classes.loading);
    } else if (this.itemsWrapper) {
      this.itemsWrapper.classList.remove(classes.loading);
    }
  }

  unload() {
    if (this.cartDrawerToggle) {
      this.cartDrawerToggle.removeEventListener('click', this.cartDrawerToggleClickEvent);
    }

    this.cartToggleButtons.forEach((button) => {
      button.removeEventListener('click', this.cartDrawerToggleClickEvent);
    });

    // Close drawers on click outside
    document.removeEventListener('mousedown', this.cartDrawerCloseEvent);

    if (this.collapsible !== null) {
      this.collapsible.onUnload();
    }
  }
}

const cartDrawer = {
  onLoad() {
    sections[this.id] = new CartDrawer();
  },
  onUnload() {
    if (typeof sections[this.id].unload === 'function') {
      sections[this.id].unload();
    }
  },
};

export default CartDrawer;
register('cart-template', cartDrawer);
